import React, { useMemo } from 'react';
import { useHostContext, HostContext, HostContextValue } from '@herx/sdk';
import { HerxCapabilityDeniedError } from '@herx/sdk';
import { notificationService } from '../../core/events/NotificationService';

interface ScopedContextProps {
  moduleId: string;
  permissions: string[];
  children: React.ReactNode;
}

const CAPABILITY_MAP: Record<string, string> = {
  'storage.readItem': 'storage:read',
  'storage.writeItem': 'storage:write',
  'mesh.chat.send': 'mesh:chat',
  'mesh.inference.run': 'mesh:inference',
  'network.fetch': 'network:external',
  'ui.openModal': 'ui:modal',
  'system.notify': 'system:notifications',
  'events.on': 'mesh:events',
  'events.emit': 'mesh:events',
  'events.once': 'mesh:events',
};

function createCapabilityProxy(
  target: any,
  moduleId: string,
  permissions: string[],
  path: string[] = []
): any {
  if (typeof target !== 'object' || target === null) {
    return target;
  }

  return new Proxy(target, {
    get(obj, prop) {
      if (typeof prop === 'string') {
        const currentPath = path.length > 0 ? `${path.join('.')}.${prop}` : prop;
        const requiredCapability = CAPABILITY_MAP[currentPath];

        if (requiredCapability && !permissions.includes(requiredCapability)) {
          // Emit diagnostic event
          notificationService.dispatch(
            moduleId,
            'Security Violation',
            `Denied access to ${currentPath}`,
            'alert'
          );
          
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('security:violation', {
              detail: { moduleId, permission: requiredCapability, capability: currentPath, timestamp: Date.now() }
            }));
          }

          throw new HerxCapabilityDeniedError(requiredCapability, currentPath, moduleId);
        }

        const value = Reflect.get(obj, prop);
        
        if (typeof value === 'function') {
          return function (...args: any[]) {
            return value.apply(obj, args);
          };
        }
        
        if (typeof value === 'object' && value !== null) {
          // Only proxy nested objects if they are likely part of our capability paths
          // We avoid proxying React components (like ChatSlot) or internal react structures
          if (prop !== 'ChatSlot' && !prop.startsWith('_')) {
            return createCapabilityProxy(value, moduleId, permissions, [...path, prop]);
          }
        }

        return value;
      }
      return Reflect.get(obj, prop);
    }
  });
}

export const ScopedHostProvider: React.FC<ScopedContextProps> = ({
  moduleId,
  permissions,
  children,
}) => {
  let rootContext: HostContextValue;
  try {
    rootContext = useHostContext();
  } catch {
    rootContext = {
      session: { currentDid: 'did:herx:p_admin', role: 'mesh_owner' },
      getApiBaseUrl: () => (typeof window !== 'undefined' ? window.location.origin : ''),
      storage: {
        getItem: async () => {},
        setItem: async () => {}
      },
      dtn: {
        publish: async () => {}
      },
      events: {
        emit: () => {},
        on: () => () => {},
        once: () => () => {}
      } as any
    };
  }

  const scopedValue = useMemo(() => {
    return createCapabilityProxy(rootContext, moduleId, permissions);
  }, [rootContext, moduleId, permissions]);

  return (
    <div className={`herx-sandbox-container module-${moduleId.replace(/\./g, '-')}`}>
      <HostContext.Provider value={scopedValue}>
        {children}
      </HostContext.Provider>
    </div>
  );
};
